// Centralized navigation link configs derived from NavMenu
// Icons use FontAwesome icon names as strings for broad compatibility.

export type NavLinkItem = {
  name: string;
  icon: string; // FontAwesome icon name
  link: string;
  requiresSuperUser?: boolean; // optional role gate
  // Path used to detect the active state, when it differs from `link` (e.g. a
  // "-switch" redirect route whose resolved destination varies by preference).
  // Matches the base path itself plus any child ("/...") or variant ("-...") paths.
  activeBase?: string;
  // Only mark active on an exact pathname match (e.g. Home's "/admin").
  exact?: boolean;
};

export function isNavItemActive(
  pathname: string,
  item: Pick<NavLinkItem, "link" | "activeBase" | "exact">,
): boolean {
  const base = item.activeBase ?? item.link;
  if (item.exact) {
    return pathname === base;
  }
  return (
    pathname === base ||
    pathname.startsWith(`${base}/`) ||
    pathname.startsWith(`${base}-`)
  );
}

// Top-level navigation (non-admin-specific)
export const navlinks: NavLinkItem[] = [
  {
    name: "Home",
    icon: "house",
    link: "/admin",
    exact: true,
  },
  {
    name: "Sites",
    icon: "table-columns",
    link: "/admin/sites-switch",
    activeBase: "/admin/sites",
  },
  {
    name: "Access Control Groups",
    icon: "universal-access",
    link: "/admin/accessControlGroups-switch",
    activeBase: "/admin/accessControlGroups",
  },
  {
    name: "Locks",
    icon: "lock",
    link: "/admin/locks-switch",
    activeBase: "/admin/locks",
  },
  {
    name: "Access Log",
    icon: "clipboard",
    link: "/admin/keyRequests",
  },
  {
    name: "Guests",
    icon: "user",
    link: "/admin/guests",
    requiresSuperUser: true,
  },
  {
    name: "Properties",
    icon: "building",
    link: "/admin/properties",
    requiresSuperUser: true,
  },
];

// Admin (private) navigation; id is optional for compatibility with callers
export const privateNavlinks = (): NavLinkItem[] => [
  {
    name: "Tenants",
    icon: "key",
    link: "/admin/tenants",
    requiresSuperUser: true,
  },
  {
    name: "Accounts",
    icon: "users",
    link: "/admin/accounts",
  },
  {
    name: "Users",
    icon: "users-rectangle",
    link: "/admin/clientUsers",
  },
  {
    name: "Shared Credentials",
    icon: "ticket",
    link: "/admin/sharedCredentials",
  },
];
